/**
 * @lines98/client/lib/i18n/resolve-locale — разрешение языка ПЕРВОГО захода по
 * ЯЗЫКУ БРАУЗЕРА (ADR-0021 D-4 + addendum-001 §B; T-I18N-3 / T-I18N-8).
 *
 * ЗАЧЕМ / WHY THIS EXISTS: язык живого пользователя (initial-state стора) выбирается
 * по precedence «явный персист > язык браузера > en» (ADR-0021 D-4, addendum §B).
 * Это ОТДЕЛЬНО от SEO-дефолта (`DEFAULT_CONTENT_LANG='en'` для canonical/x-default
 * краулера, D-3) — резолвер влияет ТОЛЬКО на живого юзера, не на canonical. Логика
 * precedence — чистая (без DOM/сети/стора), чтобы её можно было застраховать unit-ом
 * и не дублировать по call-sites (boot + sync-при-логине + редирект корня).
 *
 * ИСТОЧНИК ЯЗЫКА = ЯЗЫК БРАУЗЕРА (ADR-0021 addendum-001 §B, owner 2026-06-19;
 * гео-IP/MaxMind СНЯТ): `navigator.language` (клиент) / `Accept-Language` (сервер),
 * маппится на `UI_LANGS` через:
 *   1) прямой BCP-47 матч кода на `UI_LANGS` (`de-DE`→`de`, `zh-TW`→`zh-TW`,
 *      `zh-Hant`→`zh-TW`, `zh`/`zh-CN`→`zh-CN`);
 *   2) РЕГИОН из тега (`COUNTRY_TO_LANG`) как ВТОРИЧНЫЙ маппинг (`en-GB`→GB→en) —
 *      addendum §B: страна больше НЕ первичный сигнал, но регион из language-tag
 *      всё ещё помогает (`localeFromCountry` сохранён как точка подключения);
 *   3) не из списка → null (фолбэк на en делает резолвер выше).
 * MaxMind / IP-lookup / хранение IP — НЕ делаем (addendum §B; GDPR-нейтрально:
 * IP не читаем/не логируем). Если когда-нибудь вернётся гео-IP — за той же
 * абстракцией `localeFromCountry` (серверный слой даст код страны).
 *
 * SCOPE: чистые функции precedence (`resolveInitialLocale`) + маппинг языка браузера
 *   (`localeFromNavigatorLanguage`, `localeFromCountry`) + чтение/запись гостевого
 *   персиста localStorage (`readGuestLocale`/`writeGuestLocale`) + резолв цели
 *   редиректа голого корня по языку браузера (`resolveRootRedirectLang`, §C.3).
 * NOT IN SCOPE: глобальный стор (game-store), серверный `players.locale` (его читает/
 *   пишет caller на boot/login), активный язык движка (./runtime setLocale),
 *   применение редиректа (app.ts replaceState), коды языков (`@lines98/shared/config/seo`).
 *
 * Cross-references: ADR-0021 D-3/D-4/D-4a + addendum-001 §B/§C.3/§E
 *   (`docs/decisions/ADR-0021-i18n-global-language/`); `@lines98/shared/config/seo`
 *   (UI_LANGS / CONTENT_LANGS / COUNTRY_TO_LANG / DEFAULT_CONTENT_LANG).
 */

import {
  CONTENT_LANGS,
  COUNTRY_TO_LANG,
  DEFAULT_CONTENT_LANG,
  UI_LANGS,
  type UiLang,
} from '@lines98/shared/config/seo';

/**
 * Ключ гостевого персиста выбора языка в localStorage (ADR-0021 D-4). Гость не
 * имеет аккаунта — выбор живёт локально и побеждает гео при следующем заходе.
 * Префикс `lines98.` — общий неймспейс ключей проекта (как `lines98.player-id.v1`).
 */
export const GUEST_LOCALE_KEY = 'lines98.locale';

/** Язык-фолбэк цепочки = SEO-дефолт `en` (единый источник, не хардкод-литерал). */
const FALLBACK_LANG: UiLang = DEFAULT_CONTENT_LANG;

/** Тип-гард: строка — валидный код `UI_LANGS` (12). */
export function isUiLang(value: string | null | undefined): value is UiLang {
  return value != null && (UI_LANGS as readonly string[]).includes(value);
}

/**
 * Свести ISO-3166 код страны (alpha-2, регистр-агностично) к языку `UI_LANGS`
 * через таблицу `COUNTRY_TO_LANG`. null — страна не в таблице (caller фолбэчит на
 * en). ВТОРИЧНЫЙ маппинг (ADR-0021 addendum-001 §B): первичный сигнал — язык
 * браузера, но регион из language-tag (`en-GB`→GB) всё ещё через эту таблицу. Также
 * точка подключения, если когда-нибудь вернётся гео-IP (серверный слой даст код
 * страны — таблица одна; по умолчанию гео-IP НЕ делаем, addendum §B).
 */
export function localeFromCountry(country: string | null | undefined): UiLang | null {
  if (!country) return null;
  return COUNTRY_TO_LANG[country.toUpperCase()] ?? null;
}

/** Тип-гард: строка — индексируемый контент-язык (`CONTENT_LANGS`). */
export function isContentLangCode(value: string | null | undefined): boolean {
  return value != null && (CONTENT_LANGS as readonly string[]).includes(value);
}

/**
 * Свести BCP-47 language-tag браузера (`navigator.language`, напр. `de-DE`,
 * `zh-TW`, `en-GB`, `zh-Hant`) к языку `UI_LANGS`. Порядок попыток:
 *   1) полный тег как код `UI_LANGS` (точное совпадение, `zh-TW`→`zh-TW`);
 *   2) письменность китайского: `*-Hant`→`zh-TW`, `*-Hans`→`zh-CN`;
 *   3) базовый язык (до дефиса) как код `UI_LANGS` (`de-DE`→`de`); особый случай
 *      `zh` без региона → `zh-CN` (Simplified — преобладающая письменность);
 *   4) регион из тега через `COUNTRY_TO_LANG` (`en-GB`→GB→en);
 * не разрешилось → null (caller фолбэчит на en). Регистр нормализуется.
 */
export function localeFromNavigatorLanguage(tag: string | null | undefined): UiLang | null {
  if (!tag) return null;
  const raw = tag.trim();
  if (raw === '') return null;

  // 1) Полный тег — точный код UI_LANGS (case-insensitive: `ZH-tw` → `zh-TW`).
  const exact = matchUiLangCaseInsensitive(raw);
  if (exact) return exact;

  const parts = raw.split('-');
  const base = parts[0]!.toLowerCase();

  // 2) Китайская письменность по subtag-у (`zh-Hant-HK` → zh-TW; `zh-Hans` → zh-CN).
  if (base === 'zh') {
    const hasHant = parts.some((p) => p.toLowerCase() === 'hant');
    const hasHans = parts.some((p) => p.toLowerCase() === 'hans');
    if (hasHant) return 'zh-TW';
    if (hasHans) return 'zh-CN';
  }

  // 3) Базовый язык как код UI_LANGS (`de-DE` → `de`). `zh` без письменности/региона
  //    → Simplified (zh-CN) как преобладающий вариант.
  if (base === 'zh') return 'zh-CN';
  const byBase = matchUiLangCaseInsensitive(base);
  if (byBase) return byBase;

  // 4) Регион тега через таблицу страна→язык (`en-GB` → GB → en; `pt-BR` → BR → pt).
  const region = parts.length > 1 ? parts[parts.length - 1] : null;
  return localeFromCountry(region);
}

/** Найти код `UI_LANGS`, совпадающий с `value` без учёта регистра. */
function matchUiLangCaseInsensitive(value: string): UiLang | null {
  const lower = value.toLowerCase();
  for (const code of UI_LANGS) {
    if (code.toLowerCase() === lower) return code;
  }
  return null;
}

/**
 * Прочитать гостевой персист языка из localStorage. Возвращает валидный код
 * `UI_LANGS` или null (нет записи / повреждена / не из списка / нет доступа к
 * storage). Defensive: чтение localStorage может бросить (приватный режим).
 */
export function readGuestLocale(): UiLang | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage?.getItem(GUEST_LOCALE_KEY);
    return isUiLang(raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Записать гостевой выбор языка в localStorage. Идемпотентно; ошибки (приватный
 * режим / переполнение) проглатываются — персист best-effort, не критичен для
 * сессии (активный язык всё равно живёт в сторе в памяти).
 */
export function writeGuestLocale(lang: UiLang): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage?.setItem(GUEST_LOCALE_KEY, lang);
  } catch {
    // ignore — best-effort persist.
  }
}

/**
 * Источники для разрешения языка первого захода (ADR-0021 D-4 precedence).
 * Все поля опциональны — caller подаёт то, что у него есть на момент вызова.
 */
export interface LocaleResolutionInput {
  /**
   * Явный выбор пользователя (наивысший приоритет): гость → localStorage
   * (`readGuestLocale`); залогиненный → `players.locale` (сервер). «Последний
   * явный выбор человека побеждает» (D-4a). Невалидный/отсутствует → пропуск.
   */
  readonly explicit?: string | null;
  /**
   * Язык DEEP-LINK URL'а (между explicit и браузером, fix/i18n-unify-homepage): когда
   * пользователь заходит ПРЯМО на `/ru/` (ru-главная) или `/ru/faq/` (ru-статья), сам
   * URL — авторитетный сигнал «хочу этот язык» (сильнее языка браузера, слабее явного
   * персиста человека). Без него браузер-en перетёр бы deep-link `/ru/` обратно на en.
   * Подаёт caller (boot) из текущего pathname (landing/content lang модели B). Невалидный/
   * отсутствует → пропуск (голый en-корень `/` парсится как en — нейтрально).
   */
  readonly urlLang?: string | null;
  /**
   * Язык браузера (третий приоритет): `navigator.language` (клиент) или
   * `Accept-Language` head-значение (сервер). Резолвер разбирает BCP-47-тег +
   * регион. ADR-0021 addendum-001 §B — основной сигнал первого захода БЕЗ deep-link.
   */
  readonly navigatorLanguage?: string | null;
  /**
   * Код страны (ISO-3166) — ОПЦИОНАЛЬНЫЙ вторичный сигнал. По умолчанию НЕ
   * заполняется (гео-IP снят, addendum §B); оставлен как точка подключения, если
   * когда-нибудь вернётся гео-IP-слой. Невалидный/отсутствует → пропуск.
   */
  readonly geoCountry?: string | null;
}

/**
 * Разрешить язык первого захода по precedence (ADR-0021 D-4 + addendum-001 §B):
 *   1) явный выбор (localStorage гостя / `players.locale` залогиненного),
 *   2) ЯЗЫК БРАУЗЕРА (`navigator.language`/`Accept-Language` → UI_LANGS),
 *   3) (опц.) код страны → `COUNTRY_TO_LANG` (вторичный, по умолчанию не подаётся),
 *   4) `en` (фолбэк = `DEFAULT_CONTENT_LANG`).
 * Чистая функция: caller добывает сигналы (storage/navigator), резолвер только
 * решает. Никогда не кидает; всегда возвращает валидный `UiLang`.
 */
export function resolveInitialLocale(input: LocaleResolutionInput): UiLang {
  // 1) Явный выбор — наивысший приоритет (D-4).
  if (isUiLang(input.explicit)) return input.explicit;

  // 2) Язык DEEP-LINK URL'а (fix/i18n-unify-homepage): прямой заход на `/ru/`(-статью)
  //    — авторитетный сигнал языка, сильнее браузера. Голый en-корень `/` даёт en —
  //    нейтрально (фолбэк ниже всё равно en).
  if (isUiLang(input.urlLang)) return input.urlLang;

  // 3) Язык браузера — основной сигнал первого захода БЕЗ deep-link (addendum §B).
  const byNavigator = localeFromNavigatorLanguage(input.navigatorLanguage);
  if (byNavigator) return byNavigator;

  // 3) Код страны — вторичный (по умолчанию не подаётся; гео-IP снят).
  const byCountry = localeFromCountry(input.geoCountry);
  if (byCountry) return byCountry;

  // 4) Фолбэк en.
  return FALLBACK_LANG;
}

/**
 * Резолв ЦЕЛИ редиректа голого корня `/` по языку браузера (ADR-0021 addendum-001
 * §C.3 / §E): голый `/` ОТДАЁТ английский (контент, не редирект). Редирект на
 * `/<lang>/` происходит ТОЛЬКО если:
 *   - НЕТ явного персиста (гость без выбора / нет серверного `players.locale`), И
 *   - язык браузера = ИНДЕКСИРУЕМЫЙ (`CONTENT_LANGS`) НЕ-en.
 * Возвращает код не-en языка для редиректа ИЛИ null (остаёмся на en-корне `/`).
 * 2026-06-23: после ввода всех 8 языков `CONTENT_LANGS == UI_LANGS` — «языков-заглушек»
 * больше НЕТ, поэтому любой не-en язык браузера из UI_LANGS индексируем и редиректит.
 * Язык браузера ВНЕ UI_LANGS (не разрешается) → null (остаётся на en-корне, контент
 * всё равно en-фолбэк). Явный выбор человека ВСЕГДА
 * перекрывает (если explicit задан — null, корень не трогаем; язык применит
 * applyLocale без смены URL). Чистая функция (caller добывает сигналы + применяет
 * replaceState/302).
 */
export function resolveRootRedirectLang(input: {
  readonly explicit?: string | null;
  readonly navigatorLanguage?: string | null;
}): UiLang | null {
  // Явный выбор есть → корень не редиректим (человек уже решил; URL остаётся `/`,
  // язык применится в сторе без префикса — en-корень или язык из персиста в сторе).
  if (isUiLang(input.explicit)) return null;
  const browser = localeFromNavigatorLanguage(input.navigatorLanguage);
  // Редирект ТОЛЬКО на индексируемый не-en язык браузера.
  if (browser && browser !== DEFAULT_CONTENT_LANG && isContentLangCode(browser)) {
    return browser;
  }
  return null;
}
